import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  Stack,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Paper,
  Fade,
  Badge,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  FilterList as FilterListIcon,
  CheckCircle as CheckCircleIcon,
  Clear as ClearIcon,
  Done as DoneIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useDispatch, useSelector } from "react-redux";
import {
  setCameras,
  setDateRange,
  setSpecies,
  setFilterApplied,
} from "./filterSlice";
import { selectOrganization } from "../organization/organizationSlice";

export function Filters() {
  const [open, setOpen] = useState(false);
  const { cameras: availCameras, species: availSpecies } =
    useSelector(selectOrganization);
  const dispatch = useDispatch();

  // State for filters
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(new Date());
  const [selectedCameras, setSelectedCameras] = useState([]);
  const [selectedSpecies, setSelectedSpecies] = useState([]);
  const [selectAllCameras, setSelectAllCameras] = useState(false);
  const [selectAllSpecies, setSelectAllSpecies] = useState(false);

  // Camera and Species items with useMemo to prevent unnecessary re-renders
  const cameraItems = useMemo(() => {
    return (
      availCameras?.map((v) => ({
        id: v.id,
        name: v.description || `Camera ${v.id}`,
      })) || []
    );
  }, [availCameras]);

  const speciesItems = useMemo(() => {
    return (
      availSpecies?.map((v) => ({
        id: v.id,
        name: v.name,
      })) || []
    );
  }, [availSpecies]);

  // Handle select all cameras
  useEffect(() => {
    if (selectAllCameras) {
      const allCameraIds = cameraItems.map((c) => c.id);
      setSelectedCameras(allCameraIds);
    } else {
      setSelectedCameras([]);
    }
  }, [selectAllCameras, cameraItems]);

  // Handle select all species
  useEffect(() => {
    if (selectAllSpecies) {
      const allSpeciesIds = speciesItems.map((s) => s.id);
      setSelectedSpecies(allSpeciesIds);
    } else {
      setSelectedSpecies([]);
    }
  }, [selectAllSpecies, speciesItems]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleApplyFilter = () => {
    dispatch(
      setDateRange({
        startDate: startDate,
        endDate: endDate,
        key: "range",
      }),
    );
    dispatch(setCameras(selectedCameras));
    dispatch(setSpecies(selectedSpecies));
    dispatch(setFilterApplied(true));
    setOpen(false);
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(new Date());
    setSelectedCameras([]);
    setSelectedSpecies([]);
    setSelectAllCameras(false);
    setSelectAllSpecies(false);
  };

  const hasActiveFilters =
    selectedCameras.length > 0 ||
    selectedSpecies.length > 0 ||
    startDate !== null;
  const activeFilterCount =
    (selectedCameras.length > 0 ? 1 : 0) +
    (selectedSpecies.length > 0 ? 1 : 0) +
    (startDate !== null ? 1 : 0);

  // Original styles with gradient background
  const styles = {
    filterButton: {
      background:
        "linear-gradient(135deg, #454545 0%, rgba(129, 129, 129, 0.95) 100%)",
      borderRadius: "30px",
      padding: "4px 20px",
      color: "white",
      border: "none",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "0.875rem",
      fontWeight: 500,
      transition: "all 0.3s ease",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
      },
    },
    dialogPaper: {
      borderRadius: "24px",
      background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
      maxWidth: "600px",
      width: "100%",
    },
    dialogTitle: {
      background:
        "linear-gradient(135deg, #454545 0%, rgba(129, 129, 129, 0.95) 100%)",
      color: "white",
      padding: "20px 24px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    section: {
      padding: "20px 24px",
      borderBottom: "1px solid #e0e0e0",
    },
    sectionTitle: {
      fontSize: "16px",
      fontWeight: 600,
      color: "#454545",
      marginBottom: "16px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    selectAllBox: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "16px",
      padding: "8px 12px",
      backgroundColor: "#f5f5f5",
      borderRadius: "12px",
    },
    chipContainer: {
      display: "flex",
      flexWrap: "wrap",
      gap: "8px",
      marginTop: "12px",
    },
    chip: {
      borderRadius: "20px",
      transition: "all 0.2s ease",
      cursor: "pointer",
    },
    chipSelected: {
      background: "linear-gradient(135deg, #454545 0%, #5a5a5a 100%)",
      color: "white",
    },
    chipUnselected: {
      background: "#f0f0f0",
      color: "#666",
    },
    actionButtons: {
      display: "flex",
      gap: "12px",
      padding: "20px 24px",
      justifyContent: "flex-end",
      borderTop: "1px solid #e0e0e0",
    },
    resetButton: {
      borderRadius: "30px",
      padding: "8px 24px",
      textTransform: "none",
      fontWeight: 600,
      border: "1px solid #ddd",
      color: "#666",
      backgroundColor: "white",
      "&:hover": {
        borderColor: "#454545",
        backgroundColor: "#f5f5f5",
      },
    },
    applyButton: {
      borderRadius: "30px",
      padding: "8px 32px",
      textTransform: "none",
      fontWeight: 600,
      background: "linear-gradient(135deg, #454545 0%, #5a5a5a 100%)",
      color: "white",
      border: "none",
      "&:hover": {
        background: "linear-gradient(135deg, #5a5a5a 0%, #6b6b6b 100%)",
      },
    },
  };

  return (
    <div>
      <Tooltip title="Filter Events">
        <button onClick={handleOpen} style={styles.filterButton}>
          <FilterListIcon sx={{ fontSize: "1.2rem" }} />
          <span>Filter</span>
          {hasActiveFilters && (
            <Badge
              badgeContent={activeFilterCount}
              color="error"
              sx={{
                "& .MuiBadge-badge": {
                  backgroundColor: "#ff4444",
                },
                ml: 1,
              }}
            />
          )}
        </button>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: styles.dialogPaper }}
        TransitionComponent={Fade}
        transitionDuration={300}
      >
        {/* Dialog Header */}
        <Box sx={styles.dialogTitle}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <FilterListIcon />
            Advanced Filters
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={{
              color: "white",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Date Range Section */}
        <Box sx={styles.section}>
          <Typography sx={styles.sectionTitle}>📅 Date Range</Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack direction="row" spacing={2}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" />
                )}
                sx={{ flex: 1 }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" />
                )}
                sx={{ flex: 1 }}
              />
            </Stack>
          </LocalizationProvider>
        </Box>

        {/* Cameras Section */}
        <Box sx={styles.section}>
          <Typography sx={styles.sectionTitle}>🎥 Cameras</Typography>
          <Paper elevation={0} sx={styles.selectAllBox}>
            <FormControlLabel
              control={
                <Switch
                  checked={selectAllCameras}
                  onChange={(e) => setSelectAllCameras(e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#454545",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#454545",
                    },
                  }}
                />
              }
              label="Select All Cameras"
            />
            {selectedCameras.length > 0 && (
              <Chip
                label={`${selectedCameras.length} selected`}
                size="small"
                sx={{ bgcolor: "#e8f5e9", color: "#2e7d32" }}
              />
            )}
          </Paper>
          <Box sx={styles.chipContainer}>
            {cameraItems.map((camera) => (
              <Chip
                key={camera.id}
                label={camera.name}
                onClick={() => {
                  if (selectedCameras.includes(camera.id)) {
                    setSelectedCameras(
                      selectedCameras.filter((id) => id !== camera.id),
                    );
                    setSelectAllCameras(false);
                  } else {
                    setSelectedCameras([...selectedCameras, camera.id]);
                  }
                }}
                icon={
                  selectedCameras.includes(camera.id) ? (
                    <CheckCircleIcon />
                  ) : undefined
                }
                sx={{
                  ...styles.chip,
                  ...(selectedCameras.includes(camera.id)
                    ? styles.chipSelected
                    : styles.chipUnselected),
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Species/Tags Section */}
        <Box sx={styles.section}>
          <Typography sx={styles.sectionTitle}>🏷️ Species / Tags</Typography>
          <Paper elevation={0} sx={styles.selectAllBox}>
            <FormControlLabel
              control={
                <Switch
                  checked={selectAllSpecies}
                  onChange={(e) => setSelectAllSpecies(e.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#454545",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#454545",
                    },
                  }}
                />
              }
              label="Select All Tags"
            />
            {selectedSpecies.length > 0 && (
              <Chip
                label={`${selectedSpecies.length} selected`}
                size="small"
                sx={{ bgcolor: "#e8f5e9", color: "#2e7d32" }}
              />
            )}
          </Paper>
          <Box sx={styles.chipContainer}>
            {speciesItems.map((species) => (
              <Chip
                key={species.id}
                label={species.name}
                onClick={() => {
                  if (selectedSpecies.includes(species.id)) {
                    setSelectedSpecies(
                      selectedSpecies.filter((id) => id !== species.id),
                    );
                    setSelectAllSpecies(false);
                  } else {
                    setSelectedSpecies([...selectedSpecies, species.id]);
                  }
                }}
                icon={
                  selectedSpecies.includes(species.id) ? (
                    <CheckCircleIcon />
                  ) : undefined
                }
                sx={{
                  ...styles.chip,
                  ...(selectedSpecies.includes(species.id)
                    ? styles.chipSelected
                    : styles.chipUnselected),
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={styles.actionButtons}>
          <Button
            startIcon={<ClearIcon />}
            onClick={handleReset}
            sx={styles.resetButton}
          >
            Reset All
          </Button>
          <Button
            startIcon={<DoneIcon />}
            onClick={handleApplyFilter}
            sx={styles.applyButton}
          >
            Apply Filters
          </Button>
        </Box>
      </Dialog>
    </div>
  );
}
